// src/pages/TicketsTIPage.js - Refactored with Common Components

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useAuth from '../../hooks/useAuth';
import useFirestore from '../../hooks/useFirestore';
import styles from './HelpDesk.module.css';

// Common Components
import {
    Pagination,
    Toast,
    TableSkeleton,
    EmptyState,
} from '../../components/common';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiFileText,
    FiSearch,
    FiCalendar,
    FiTag,
    FiList,
    FiEye,
    FiClock,
    FiUser,
    FiFlag,
    FiUsers,
    FiSend,
    FiPlus,
    FiMessageSquare,
    FiMail,
    FiChevronDown,
    FiChevronUp,
    FiFilter,
    FiClock as FiClockIcon,
    FiCheckCircle,
    FiCircle,
    FiRadio,
    FiMessageCircle,
    FiMinimize2,
    FiMaximize2,
} from 'react-icons/fi';
import { FaTicketAlt } from 'react-icons/fa';

// Constants
const TICKETS_COLLECTION = 'help-desk-tickets';
const STATUS_OPTIONS = ['Aberto', 'Em Progresso', 'Fechado'];
const ITEMS_PER_PAGE = 10;

const PRIORITY_OPTIONS = [
    { value: 'critico', label: 'Crítico', icon: '🔴' },
    { value: 'alto', label: 'Alta', icon: '🟠' },
    { value: 'medio', label: 'Média', icon: '🟡' },
    { value: 'baixo', label: 'Baixa', icon: '🟢' },
];

const IMPACT_OPTIONS = [
    { value: 'todos', label: 'Todos os usuários' },
    { value: 'departamento', label: 'Departamento' },
    { value: 'equipe', label: 'Equipe' },
    { value: 'individual', label: 'Individual' },
];

const URGENCY_OPTIONS = [
    { value: 'imediato', label: 'Imediato' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'normal', label: 'Normal' },
    { value: 'baixa', label: 'Baixa' },
];

const CATEGORY_OPTIONS = [
    { value: 'acesso', label: 'Acesso e Permissões' },
    { value: 'sistema', label: 'Erro no Sistema' },
    { value: 'duvida', label: 'Dúvida sobre Funcionalidade' },
    { value: 'melhoria', label: 'Sugestão de Melhoria' },
    { value: 'dados', label: 'Problema com Dados' },
    { value: 'integracao', label: 'Integração com Outros Sistemas' },
    { value: 'hardware', label: 'Problema de Hardware' },
    { value: 'rede', label: 'Problema de Rede' },
    { value: 'seguranca', label: 'Segurança' },
    { value: 'outro', label: 'Outro' },
];

// ============================================
// HELPERS
// ============================================

const getPriorityDisplay = (value) => {
    const found = PRIORITY_OPTIONS.find(p => p.value === value?.toLowerCase());
    return found || { label: value || 'N/A', icon: '📊' };
};

const getCategoryDisplay = (value) => {
    const found = CATEGORY_OPTIONS.find(c => c.value === value);
    return found || { label: value || 'N/A' };
};

const getUrgencyDisplay = (value) => {
    const found = URGENCY_OPTIONS.find(u => u.value === value);
    return found || { label: value || 'N/A' };
};

const getImpactDisplay = (value) => {
    const found = IMPACT_OPTIONS.find(i => i.value === value);
    return found || { label: value || 'N/A' };
};

const formatTimeSpent = (totalMinutes) => {
    if (!totalMinutes || totalMinutes < 0) return '0min';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
        return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
    }
    return `${minutes}min`;
};

const getTimeInStatus = (ticket) => {
    if (!ticket?.updatedAt?.toDate) return 'N/A';
    const diff = Date.now() - ticket.updatedAt.toDate().getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}min`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
};

// ============================================
// BADGE COMPONENT
// ============================================

const Badge = ({ text, type }) => {
    const getStatusClass = () => {
        if (type === 'status') {
            const classMap = {
                'Aberto': `${styles.statusBadge} ${styles.open}`,
                'Em Progresso': `${styles.statusBadge} ${styles.inProgress}`,
                'Fechado': `${styles.statusBadge} ${styles.closed}`,
            };
            return classMap[text] || `${styles.statusBadge} ${styles.open}`;
        }
        if (type === 'priority') {
            const classMap = {
                'Crítico': `${styles.priorityBadge} ${styles.critical}`,
                'Alta': `${styles.priorityBadge} ${styles.high}`,
                'Média': `${styles.priorityBadge} ${styles.medium}`,
                'Baixa': `${styles.priorityBadge} ${styles.low}`,
            };
            return classMap[text] || `${styles.priorityBadge} ${styles.medium}`;
        }
        return '';
    };

    return <span className={getStatusClass()}>{text}</span>;
};

// ============================================
// CHAT PANEL COMPONENT - TI Version
// ============================================

const ChatPanel = ({
    ticket,
    onClose,
    onSendMessage,
    onCloseTicket,
    currentUser,
    loading,
    isMinimized,
    onToggleMinimize
}) => {
    const [message, setMessage] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const followUps = ticket?.followUps || [];
    const isClosed = ticket?.status === 'Fechado';

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [followUps]);

    useEffect(() => {
        if (!isMinimized && !isClosed) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isMinimized, isClosed]);

    const handleSend = async () => {
        if (!message.trim() || loading || isClosed) return;
        await onSendMessage(message.trim());
        setMessage('');
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
        if (e.key === 'Escape') {
            onClose();
        }
    };

    const handleCloseTicket = async () => {
        if (!message.trim()) {
            alert('Adicione uma mensagem de resolução.');
            return;
        }
        if (window.confirm('Fechar este ticket?')) {
            setIsClosing(true);
            await onCloseTicket(message.trim());
            setIsClosing(false);
            onClose();
        }
    };

    if (!ticket) return null;

    return (
        <div className={`${styles.chatPanel} ${isMinimized ? styles.chatPanelMinimized : ''}`}>
            <div className={styles.chatHeader}>
                <div className={styles.chatHeaderInfo}>
                    <div className={styles.chatHeaderAvatar}>
                        <FiMessageCircle size={16} />
                    </div>
                    <div>
                        <div className={styles.chatHeaderTitle}>
                            #{ticket.id.substring(0, 6)}
                            {isClosed && (
                                <span className={styles.chatHeaderClosedBadge}>
                                    <FiCheckCircle size={12} /> Fechado
                                </span>
                            )}
                        </div>
                        <div className={styles.chatHeaderSubtitle}>
                            {ticket.userName}
                            <span className={styles.chatHeaderDot} />
                            <span className={isClosed ? styles.statusClosed : styles.statusOpen}>
                                {ticket.status}
                            </span>
                            {isClosed && (
                                <>
                                    <span className={styles.chatHeaderDot} />
                                    <span className={styles.chatHeaderDate}>
                                        {ticket.updatedAt?.toDate?.()?.toLocaleDateString('pt-BR')}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.chatHeaderActions}>
                    <button
                        onClick={onToggleMinimize}
                        className={styles.chatIconBtn}
                        aria-label={isMinimized ? "Expandir chat" : "Minimizar chat"}
                    >
                        {isMinimized ? <FiMaximize2 size={16} /> : <FiMinimize2 size={16} />}
                    </button>
                    <button
                        onClick={onClose}
                        className={styles.chatIconBtn}
                        aria-label="Fechar chat"
                    >
                        <FiX size={18} />
                    </button>
                </div>
            </div>

            {!isMinimized && (
                <>
                    <div className={styles.chatMessages}>
                        {followUps.length === 0 ? (
                            <div className={styles.chatEmpty}>
                                <div className={styles.chatEmptyIcon}>💬</div>
                                <p>Nenhuma mensagem</p>
                            </div>
                        ) : (
                            followUps.map((msg, index) => {
                                const isFromUser = msg.isUserReply;
                                const isClosure = msg.type === 'closure';

                                return (
                                    <div
                                        key={index}
                                        className={`${styles.chatMessage} ${isFromUser ? styles.chatMessageUser : styles.chatMessageSupport
                                            } ${isClosure ? styles.chatMessageClosure : ''}`}
                                    >
                                        <div className={styles.chatMessageMeta}>
                                            <span className={styles.chatMessageName}>
                                                {isFromUser ? ticket.userName : 'Suporte'}
                                            </span>
                                            <span className={styles.chatMessageTime}>
                                                {msg.sentAt?.toDate?.()?.toLocaleTimeString('pt-BR', {
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }) || ''}
                                            </span>
                                        </div>
                                        <div className={styles.chatMessageText}>
                                            {msg.message}
                                        </div>
                                        {isClosure && (
                                            <div className={styles.chatMessageBadge}>
                                                <FiCheckCircle size={12} /> Resolvido
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {isClosed ? (
                        <div className={styles.chatReadOnly}>
                            <div className={styles.chatReadOnlyIcon}>
                                <FiEye size={16} />
                            </div>
                            <span>Modo de visualização - Ticket fechado</span>
                            <button
                                onClick={onClose}
                                className={styles.chatReadOnlyCloseBtn}
                            >
                                Fechar
                            </button>
                        </div>
                    ) : (
                        <div className={styles.chatInput}>
                            <div className={styles.chatInputRow}>
                                <textarea
                                    ref={inputRef}
                                    className={styles.chatInputField}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Digite sua mensagem..."
                                    rows="1"
                                    disabled={loading || isClosing}
                                />
                                <button
                                    onClick={handleSend}
                                    disabled={loading || isClosing || !message.trim()}
                                    className={styles.chatSendBtn}
                                >
                                    {loading ? <span className={styles.buttonSpinner} /> : <FiSend size={18} />}
                                </button>
                            </div>
                            <div className={styles.chatInputFooter}>
                                <button
                                    onClick={handleCloseTicket}
                                    disabled={loading || isClosing || !message.trim()}
                                    className={styles.chatCloseBtn}
                                >
                                    <FiCheckCircle size={14} /> Fechar ticket
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ============================================
// FILTER CHIP
// ============================================

const FilterChip = ({ label, active, onClick, icon, count, ariaLabel }) => (
    <button
        className={`${styles.filterChip} ${active ? styles.filterChipActive : ''}`}
        onClick={onClick}
        role="tab"
        aria-selected={active}
        aria-label={ariaLabel || label}
    >
        {icon && <span className={styles.filterChipIcon} aria-hidden="true">{icon}</span>}
        {label}
        {count !== undefined && count > 0 && (
            <span className={styles.filterCount} aria-hidden="true">({count})</span>
        )}
    </button>
);

// ============================================
// STATS CARD
// ============================================

const StatsCard = ({ label, value, icon, color, ariaLabel }) => (
    <div
        className={styles.statCard}
        style={{ borderLeft: `4px solid ${color}` }}
        aria-label={ariaLabel || `${label}: ${value}`}
    >
        <div className={styles.statIcon} aria-hidden="true">{icon}</div>
        <div className={styles.statValue}>{value}</div>
        <div className={styles.statLabel}>{label}</div>
    </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

const TicketsTIPage = () => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const userRole = currentUser?.role;

    // State
    const [toast, setToast] = useState(null);
    const [loadingAction, setLoadingAction] = useState(null);
    const [filterStatus, setFilterStatus] = useState('todos');
    const [filterPriority, setFilterPriority] = useState('todos');
    const [filterAssigned, setFilterAssigned] = useState('todos');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Chat state
    const [activeChat, setActiveChat] = useState(null);
    const [isChatMinimized, setIsChatMinimized] = useState(false);

    const {
        data: ticketsRaw,
        loading: listLoading,
        error: listError,
        updateDocument
    } = useFirestore(TICKETS_COLLECTION, 'createdAt', 'desc');

    // ============================================
    // TOAST SYSTEM
    // ============================================

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
    }, []);

    // ============================================
    // TICKETS DATA
    // ============================================

    const tickets = useMemo(() => {
        if (!ticketsRaw) return [];
        return [...ticketsRaw].sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(0);
            const dateB = b.createdAt?.toDate?.() || new Date(0);
            return dateB - dateA;
        });
    }, [ticketsRaw]);

    // ============================================
    // STATS
    // ============================================

    const stats = useMemo(() => ({
        total: tickets?.length || 0,
        abertos: tickets?.filter(t => t.status === 'Aberto').length || 0,
        emProgresso: tickets?.filter(t => t.status === 'Em Progresso').length || 0,
        fechados: tickets?.filter(t => t.status === 'Fechado').length || 0,
        criticos: tickets?.filter(t => {
            const priority = t.prioridadeRaw?.toLowerCase() || t.prioridade?.toLowerCase();
            return priority === 'critico' || priority === 'critical';
        }).length || 0,
        meusTickets: tickets?.filter(t => t.assignedToEmail === currentUser?.email && t.status !== 'Fechado').length || 0,
        naoAtribuidos: tickets?.filter(t => !t.assignedToEmail && t.status !== 'Fechado').length || 0,
        comFollowUp: tickets?.filter(t => t.followUps && t.followUps.length > 0).length || 0,
    }), [tickets, currentUser]);

    // ============================================
    // FILTERS
    // ============================================

    const filteredTickets = useMemo(() => {
        if (!tickets) return [];

        return tickets.filter(ticket => {
            const statusMatch = filterStatus === 'todos' || ticket.status === filterStatus;

            const priorityMatch = filterPriority === 'todos' ||
                ticket.prioridadeRaw?.toLowerCase() === filterPriority ||
                ticket.prioridade?.toLowerCase() === filterPriority;

            let assignedMatch = true;
            if (filterAssigned === 'meus') {
                assignedMatch = ticket.assignedToEmail === currentUser?.email;
            } else if (filterAssigned === 'nao_atribuidos') {
                assignedMatch = !ticket.assignedToEmail;
            }

            let searchMatch = true;
            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase().trim();
                searchMatch =
                    ticket.assunto?.toLowerCase().includes(term) ||
                    ticket.descricao?.toLowerCase().includes(term) ||
                    ticket.id?.toLowerCase().includes(term) ||
                    ticket.userName?.toLowerCase().includes(term) ||
                    ticket.userEmail?.toLowerCase().includes(term);
            }

            return statusMatch && priorityMatch && assignedMatch && searchMatch;
        });
    }, [tickets, filterStatus, filterPriority, filterAssigned, searchTerm, currentUser]);

    // ============================================
    // PAGINATION
    // ============================================

    const totalPages = Math.ceil(filteredTickets.length / ITEMS_PER_PAGE);
    const paginatedTickets = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredTickets.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredTickets, currentPage]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filterStatus, filterPriority, filterAssigned, searchTerm]);

    // ============================================
    // CHAT HANDLERS
    // ============================================

    const handleOpenChat = useCallback((ticket) => {
        setActiveChat(ticket);
        setIsChatMinimized(false);
    }, []);

    const handleCloseChat = useCallback(() => {
        setActiveChat(null);
        setIsChatMinimized(false);
    }, []);

    const handleToggleMinimize = useCallback(() => {
        setIsChatMinimized(prev => !prev);
    }, []);

    const handleSendChatMessage = useCallback(async (message) => {
        if (!activeChat) return;

        setLoadingAction(activeChat.id);

        const followUpEntry = {
            message: message,
            sentBy: currentUser.nome || currentUser.email,
            sentByEmail: currentUser.email,
            sentAt: new Date(),
            type: 'followup',
            isUserReply: false,
        };

        const updateData = {
            updatedAt: new Date(),
            updatedBy: currentUser.email,
            followUps: [...(activeChat.followUps || []), followUpEntry],
            history: [
                ...(activeChat.history || []),
                {
                    action: 'followup_sent',
                    by: currentUser.nome || currentUser.email,
                    at: new Date(),
                    details: 'Mensagem enviada via chat'
                }
            ]
        };

        try {
            await updateDocument(activeChat.id, updateData);
            setActiveChat(prev => ({
                ...prev,
                ...updateData,
                followUps: [...(prev.followUps || []), followUpEntry]
            }));
            showToast('Mensagem enviada! 📨', 'success');
        } catch (err) {
            console.error("Erro ao enviar mensagem:", err);
            showToast('Erro ao enviar mensagem', 'error');
        } finally {
            setLoadingAction(null);
        }
    }, [activeChat, currentUser, updateDocument, showToast]);

    const handleCloseTicketFromChat = useCallback(async (message) => {
        if (!activeChat) return;

        setLoadingAction(activeChat.id);

        const followUpEntry = {
            message: message,
            sentBy: currentUser.nome || currentUser.email,
            sentByEmail: currentUser.email,
            sentAt: new Date(),
            type: 'closure',
            isUserReply: false,
        };

        const updateData = {
            status: 'Fechado',
            resolution: message,
            updatedAt: new Date(),
            updatedBy: currentUser.email,
            followUps: [...(activeChat.followUps || []), followUpEntry],
            timeSpentMinutes: activeChat.timeSpentMinutes || 0,
            history: [
                ...(activeChat.history || []),
                {
                    action: 'closed_by_ti',
                    by: currentUser.nome || currentUser.email,
                    at: new Date(),
                    details: 'Ticket fechado pelo suporte'
                }
            ]
        };

        try {
            await updateDocument(activeChat.id, updateData);
            showToast('Ticket fechado com sucesso! ✅', 'success');
            handleCloseChat();
        } catch (err) {
            console.error("Erro ao fechar ticket:", err);
            showToast('Erro ao fechar ticket', 'error');
        } finally {
            setLoadingAction(null);
        }
    }, [activeChat, currentUser, updateDocument, showToast, handleCloseChat]);

    // ============================================
    // ASSIGN TICKET
    // ============================================

    const handleAssign = useCallback(async (ticket) => {
        if (!window.confirm(`Assumir ticket #${ticket.id.substring(0, 8)}?`)) return;

        setLoadingAction(ticket.id);
        const updateData = {
            assignedToEmail: currentUser.email,
            assignedToName: currentUser.nome || currentUser.email,
            status: ticket.status === 'Aberto' ? 'Em Progresso' : ticket.status,
            updatedAt: new Date(),
            history: [
                ...(ticket.history || []),
                {
                    action: 'assigned',
                    by: currentUser.nome || currentUser.email,
                    at: new Date(),
                    details: 'Ticket assumido'
                }
            ]
        };

        try {
            await updateDocument(ticket.id, updateData);
            showToast('Ticket assumido com sucesso!', 'success');
        } catch (err) {
            console.error("Erro ao assumir ticket:", err);
            showToast('Erro ao assumir ticket', 'error');
        } finally {
            setLoadingAction(null);
        }
    }, [currentUser, updateDocument, showToast]);

    // ============================================
    // UPDATE STATUS
    // ============================================

    const handleUpdateStatus = useCallback(async (ticketId, newStatus, currentTotalTime) => {
        if (newStatus === 'Fechado') {
            const ticket = tickets.find(t => t.id === ticketId);
            if (ticket) {
                handleOpenChat(ticket);
            }
            return;
        }

        const timeInput = prompt(`Digite o tempo gasto (em minutos) para atualizar para "${newStatus}":`);
        if (timeInput === null) return;

        const timeMinutes = parseInt(timeInput, 10);
        if (isNaN(timeMinutes) || timeMinutes < 0) {
            showToast('Tempo inválido!', 'warning');
            return;
        }

        if (!window.confirm(`Confirmar mudança para "${newStatus}" com ${timeMinutes}min?`)) return;

        setLoadingAction(ticketId);

        const ticket = tickets.find(t => t.id === ticketId);
        const updateData = {
            status: newStatus,
            timeSpentMinutes: (currentTotalTime || 0) + timeMinutes,
            updatedBy: currentUser.email,
            updatedAt: new Date(),
            history: [
                ...(ticket?.history || []),
                {
                    action: 'status_changed',
                    by: currentUser.nome || currentUser.email,
                    at: new Date(),
                    details: `Status alterado para ${newStatus}`
                }
            ]
        };

        try {
            await updateDocument(ticketId, updateData);
            showToast(`Status alterado para "${newStatus}"!`, 'success');
        } catch (err) {
            console.error("Erro ao atualizar:", err);
            showToast('Erro ao atualizar ticket', 'error');
        } finally {
            setLoadingAction(null);
        }
    }, [currentUser, tickets, updateDocument, showToast, handleOpenChat]);

    // ============================================
    // ACCESS CHECK
    // ============================================

    if (userRole !== 'TI' && userRole !== 'Diretoria') {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.errorContainer} role="alert">
                    <div className={styles.emptyIcon} aria-hidden="true"><FiX size={48} /></div>
                    <h3 className={styles.emptyTitle}>{t('pages.helpdesk.accessDenied')}</h3>
                    <p className={styles.emptyMessage}>{t('pages.helpdesk.accessDeniedMessage')}</p>
                </div>
            </div>
        );
    }

    // ============================================
    // MAIN RENDER
    // ============================================

    return (
        <div className={styles.pageContainer}>
            {/* Toast using common component */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                    duration={5000}
                />
            )}

            {/* Chat Panel */}
            {activeChat && (
                <ChatPanel
                    ticket={activeChat}
                    onClose={handleCloseChat}
                    onSendMessage={handleSendChatMessage}
                    onCloseTicket={handleCloseTicketFromChat}
                    currentUser={currentUser}
                    loading={loadingAction === activeChat.id}
                    isMinimized={isChatMinimized}
                    onToggleMinimize={handleToggleMinimize}
                />
            )}

            {/* Quick Filters */}
            <div className={styles.quickFilters} role="tablist" aria-label="Filtros rápidos">
                <FilterChip
                    label="Todos"
                    active={filterStatus === 'todos' && filterPriority === 'todos' && filterAssigned === 'todos'}
                    onClick={() => {
                        setFilterStatus('todos');
                        setFilterPriority('todos');
                        setFilterAssigned('todos');
                    }}
                    ariaLabel="Mostrar todos os tickets"
                />
                <FilterChip
                    label="🔴 Abertos"
                    active={filterStatus === 'Aberto'}
                    onClick={() => setFilterStatus('Aberto')}
                    ariaLabel="Mostrar apenas tickets abertos"
                />
                <FilterChip
                    label="🟡 Em Progresso"
                    active={filterStatus === 'Em Progresso'}
                    onClick={() => setFilterStatus('Em Progresso')}
                    ariaLabel="Mostrar apenas tickets em progresso"
                />
                <FilterChip
                    label="✅ Fechados"
                    active={filterStatus === 'Fechado'}
                    onClick={() => setFilterStatus('Fechado')}
                    ariaLabel="Mostrar apenas tickets fechados"
                />
                <FilterChip
                    label="🔴 Críticos"
                    active={filterPriority === 'critico'}
                    onClick={() => setFilterPriority('critico')}
                    ariaLabel="Mostrar apenas tickets críticos"
                />
                <FilterChip
                    label="👤 Meus Tickets"
                    active={filterAssigned === 'meus'}
                    onClick={() => setFilterAssigned('meus')}
                    ariaLabel="Mostrar apenas meus tickets"
                />
                <FilterChip
                    label="📭 Não Atribuídos"
                    active={filterAssigned === 'nao_atribuidos'}
                    onClick={() => setFilterAssigned('nao_atribuidos')}
                    ariaLabel="Mostrar apenas tickets não atribuídos"
                />
                <FilterChip
                    label="💬 Com Conversa"
                    active={false}
                    onClick={() => {
                        showToast('Filtro de conversas em breve!', 'info');
                    }}
                    ariaLabel="Mostrar apenas tickets com conversa"
                />
            </div>

            {/* Table */}
            {listLoading ? (
                <TableSkeleton columns={7} rows={5} />
            ) : listError ? (
                <div className={styles.errorContainer} role="alert">
                    <p className={styles.errorText}><FiAlertCircle size={20} aria-hidden="true" /> {t('pages.helpdesk.error', { error: listError.message })}</p>
                </div>
            ) : filteredTickets.length === 0 ? (
                <EmptyState
                    icon="🎫"
                    title={tickets?.length === 0 ? 'Nenhum ticket registrado' : 'Nenhum ticket encontrado com os filtros atuais'}
                    description={tickets?.length === 0 ? 'Os tickets aparecerão aqui quando forem criados pelos usuários.' : 'Tente ajustar os filtros para visualizar mais tickets.'}
                />
            ) : (
                <>
                    <div className={styles.tableContainer}>
                        <div className={styles.tableWrapper}>
                            <table className={styles.table} aria-label="Lista de tickets">
                                <thead>
                                    <tr>
                                        <th scope="col">ID</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Prioridade</th>
                                        <th scope="col">Solicitante</th>
                                        <th scope="col">Assunto</th>
                                        <th scope="col">Responsável</th>
                                        <th scope="col">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedTickets.map((ticket) => {
                                        const isAssignedToMe = ticket.assignedToEmail === currentUser.email;
                                        const isAssigned = !!ticket.assignedToEmail;
                                        const isLoading = loadingAction === ticket.id;
                                        const priorityInfo = getPriorityDisplay(ticket.prioridadeRaw || ticket.prioridade?.toLowerCase());
                                        const isCritical = ticket.prioridade?.toLowerCase() === 'critico' || ticket.prioridadeRaw?.toLowerCase() === 'critico';
                                        const hasFollowUps = (ticket.followUps && ticket.followUps.length > 0);
                                        const hasUnreadReplies = ticket.followUps?.some(f => !f.isUserReply) || false;
                                        const timeInStatus = getTimeInStatus(ticket);
                                        const isChatOpen = activeChat?.id === ticket.id;

                                        return (
                                            <tr key={ticket.id} className={isCritical ? styles.highlightedRow : ''}>
                                                <td className={styles.ticketId}>
                                                    #{ticket.id.substring(0, 8)}
                                                    {hasUnreadReplies && ticket.status !== 'Fechado' && (
                                                        <span className={styles.newReplyBadge}>
                                                            <FiMail size={10} aria-hidden="true" /> Nova
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <Badge text={ticket.status} type="status" />
                                                </td>
                                                <td><Badge text={priorityInfo.label} type="priority" /></td>
                                                <td>
                                                    <div className={styles.userCell}>
                                                        <FiUser size={12} aria-hidden="true" /> {ticket.userName}
                                                        <div className={styles.userRole}>{ticket.userRole}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={styles.subjectCell}>
                                                        <span>{ticket.assunto.length > 35 ? ticket.assunto.substring(0, 35) + '...' : ticket.assunto}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    {ticket.assignedToName ? (
                                                        <div>
                                                            <div className={styles.assignedUser}>
                                                                <FiUser size={12} aria-hidden="true" /> {ticket.assignedToName.length > 15 ? ticket.assignedToName.substring(0, 15) + '...' : ticket.assignedToName}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className={styles.unassigned}>Não atribuído</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className={styles.actionGroup}>
                                                        {!isAssigned && ticket.status !== 'Fechado' && (
                                                            <button
                                                                onClick={() => handleAssign(ticket)}
                                                                disabled={isLoading}
                                                                className={styles.assignBtn}
                                                                aria-label={`Assumir ticket ${ticket.assunto}`}
                                                            >
                                                                {isLoading ? <FiRefreshCw size={12} className={styles.spinning} aria-hidden="true" /> : <FiUser size={12} aria-hidden="true" />}
                                                                Assumir
                                                            </button>
                                                        )}

                                                        {isAssigned && (isAssignedToMe || userRole === 'Diretoria') && (
                                                            <button
                                                                onClick={() => handleOpenChat(ticket)}
                                                                disabled={isLoading}
                                                                className={`${styles.followUpQuickBtn} ${isChatOpen ? styles.chatActive : ''} ${ticket.status === 'Fechado' ? styles.viewOnlyChat : ''}`}
                                                                title={ticket.status === 'Fechado' ? "Ver histórico do chat" : "Abrir chat"}
                                                                aria-label={ticket.status === 'Fechado' ? `Ver histórico do ticket ${ticket.assunto}` : `Abrir chat do ticket ${ticket.assunto}`}
                                                            >
                                                                {ticket.status === 'Fechado' ? (
                                                                    <FiEye size={14} aria-hidden="true" />
                                                                ) : (
                                                                    <FiMessageCircle size={14} aria-hidden="true" />
                                                                )}
                                                                {isChatOpen && <span className={styles.chatIndicator} />}
                                                            </button>
                                                        )}

                                                        {isAssigned && !isAssignedToMe && ticket.status !== 'Fechado' && (
                                                            <span className={styles.assignedBy}>
                                                                {ticket.assignedToName?.split(' ')[0] || 'Atribuído'}
                                                            </span>
                                                        )}

                                                        {isAssigned && ticket.status === 'Fechado' && (
                                                            <span className={styles.completedBadge}>
                                                                <FiCheck size={14} aria-hidden="true" />
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination using common component */}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={filteredTickets.length}
                        onPageChange={setCurrentPage}
                        itemsLabel="tickets"
                    />
                </>
            )}
        </div>
    );
};

export default TicketsTIPage;